import * as THREE from 'three';
import { fpsAudio } from '../audio/FPSAudio';

export type WeaponType = 'enforcer' | 'shock' | 'pulse' | 'flak' | 'rocket' | 'sniper';

export interface WeaponDef {
  type: WeaponType;
  name: string;
  fireRate: number; // in seconds
  damage: number;
  ammo: number;
  maxAmmo: number;
  color: number;
  slot: number;
}

export interface Projectile {
  mesh: THREE.Object3D;
  velocity: THREE.Vector3;
  damage: number;
  radius: number;
  ownerId: string;
  type: 'bullet' | 'shock_orb' | 'flak_shard' | 'rocket' | 'pulse';
  lifetime: number;
  isBouncing?: boolean;
}

export interface Explosion {
  position: THREE.Vector3;
  radius: number;
  damage: number;
  ownerId: string;
}

export class WeaponSystem {
  public projectiles: Projectile[] = [];
  public beams: { mesh: THREE.Mesh; lifetime: number }[] = [];
  public activeExplosions: Explosion[] = [];

  private scene: THREE.Scene;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  public fire(
    weaponType: WeaponType,
    ownerId: string,
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    isAlt: boolean = false
  ): boolean {
    const dir = direction.clone().normalize();

    switch (weaponType) {
      case 'enforcer': {
        fpsAudio.playSound('enforcer');
        const geo = new THREE.SphereGeometry(0.12, 6, 6);
        const mat = new THREE.MeshBasicMaterial({ color: 0xffff00 });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.copy(origin);
        this.scene.add(mesh);

        this.projectiles.push({
          mesh,
          velocity: dir.multiplyScalar(130),
          damage: 22,
          radius: 0.3,
          ownerId,
          type: 'bullet',
          lifetime: 2.0,
        });
        break;
      }

      case 'shock': {
        if (!isAlt) {
          // Primary: Instant Hitscan Lightning Beam
          fpsAudio.playSound('shock');
          const beamGeo = new THREE.CylinderGeometry(0.06, 0.06, 80, 8);
          const beamMat = new THREE.MeshBasicMaterial({
            color: 0x00fff2,
            transparent: true,
            opacity: 0.9,
          });
          const beamMesh = new THREE.Mesh(beamGeo, beamMat);
          beamMesh.position.copy(origin).add(dir.clone().multiplyScalar(40));
          beamMesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
          this.scene.add(beamMesh);
          this.beams.push({ mesh: beamMesh, lifetime: 0.12 });

          // Also check if the hitscan beam strikes any floating shock orbs (SHOCK COMBO!)
          for (let i = this.projectiles.length - 1; i >= 0; i--) {
            const p = this.projectiles[i];
            if (p.type === 'shock_orb') {
              const toOrb = p.mesh.position.clone().sub(origin);
              const projDist = toOrb.dot(dir);
              if (projDist > 0 && projDist < 80) {
                const perpDist = toOrb.clone().sub(dir.clone().multiplyScalar(projDist)).length();
                if (perpDist < 1.8) {
                  // TRIGGER SHOCK COMBO!
                  fpsAudio.playSound('explosion');
                  this.triggerShockCombo(p.mesh.position, ownerId);
                  this.scene.remove(p.mesh);
                  this.projectiles.splice(i, 1);
                  break;
                }
              }
            }
          }
        } else {
          // Alt: Floating Shock Plasma Orb
          fpsAudio.playSound('shock');
          const orbGeo = new THREE.SphereGeometry(0.38, 12, 12);
          const orbMat = new THREE.MeshBasicMaterial({ color: 0x00aaff });
          const orbMesh = new THREE.Mesh(orbGeo, orbMat);
          orbMesh.position.copy(origin);
          this.scene.add(orbMesh);

          this.projectiles.push({
            mesh: orbMesh,
            velocity: dir.multiplyScalar(28),
            damage: 55,
            radius: 0.6,
            ownerId,
            type: 'shock_orb',
            lifetime: 3.5,
          });
        }
        break;
      }

      case 'pulse': {
        fpsAudio.playSound('pulse');
        const geo = new THREE.SphereGeometry(0.18, 8, 8);
        const mat = new THREE.MeshBasicMaterial({ color: 0x39ff14 });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.copy(origin);
        this.scene.add(mesh);

        this.projectiles.push({
          mesh,
          velocity: dir.multiplyScalar(95),
          damage: 14,
          radius: 0.35,
          ownerId,
          type: 'pulse',
          lifetime: 1.8,
        });
        break;
      }

      case 'flak': {
        fpsAudio.playSound('flak');
        if (!isAlt) {
          // Primary: 8 Shrapnel shards in a cone
          for (let i = 0; i < 8; i++) {
            const spreadDir = dir
              .clone()
              .add(
                new THREE.Vector3(
                  (Math.random() - 0.5) * 0.18,
                  (Math.random() - 0.5) * 0.18,
                  (Math.random() - 0.5) * 0.18
                )
              )
              .normalize();

            const geo = new THREE.BoxGeometry(0.14, 0.14, 0.22);
            const mat = new THREE.MeshBasicMaterial({ color: 0xffaa00 });
            const mesh = new THREE.Mesh(geo, mat);
            mesh.position.copy(origin);
            this.scene.add(mesh);

            this.projectiles.push({
              mesh,
              velocity: spreadDir.multiplyScalar(75),
              damage: 16,
              radius: 0.28,
              ownerId,
              type: 'flak_shard',
              lifetime: 1.4,
              isBouncing: true,
            });
          }
        } else {
          // Alt: Flak Bomb
          const geo = new THREE.SphereGeometry(0.35, 8, 8);
          const mat = new THREE.MeshBasicMaterial({ color: 0xff6600 });
          const mesh = new THREE.Mesh(geo, mat);
          mesh.position.copy(origin);
          this.scene.add(mesh);

          this.projectiles.push({
            mesh,
            velocity: dir.multiplyScalar(45).add(new THREE.Vector3(0, 4, 0)),
            damage: 90,
            radius: 0.6,
            ownerId,
            type: 'rocket',
            lifetime: 2.5,
          });
        }
        break;
      }

      case 'rocket': {
        fpsAudio.playSound('rocket');
        const rocketGroup = new THREE.Group();
        rocketGroup.position.copy(origin);
        rocketGroup.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir);

        // Rocket Body
        const bodyGeo = new THREE.CylinderGeometry(0.12, 0.12, 0.8, 8);
        const bodyMat = new THREE.MeshBasicMaterial({ color: 0xff3300 });
        const body = new THREE.Mesh(bodyGeo, bodyMat);
        body.rotation.x = Math.PI / 2;
        rocketGroup.add(body);

        // Rocket Flame
        const flameGeo = new THREE.ConeGeometry(0.1, 0.4, 6);
        const flameMat = new THREE.MeshBasicMaterial({ color: 0xffff00 });
        const flame = new THREE.Mesh(flameGeo, flameMat);
        flame.position.z = -0.5;
        flame.rotation.x = -Math.PI / 2;
        rocketGroup.add(flame);

        this.scene.add(rocketGroup);

        this.projectiles.push({
          mesh: rocketGroup,
          velocity: dir.multiplyScalar(65),
          damage: 100,
          radius: 0.5,
          ownerId,
          type: 'rocket',
          lifetime: 3.5,
        });
        break;
      }

      case 'sniper': {
        fpsAudio.playSound('sniper');
        // Tracer beam
        const tracerGeo = new THREE.CylinderGeometry(0.04, 0.04, 120, 6);
        const tracerMat = new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.95,
        });
        const tracer = new THREE.Mesh(tracerGeo, tracerMat);
        tracer.position.copy(origin).add(dir.clone().multiplyScalar(60));
        tracer.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
        this.scene.add(tracer);
        this.beams.push({ mesh: tracer, lifetime: 0.08 });
        break;
      }
    }

    return true;
  }

  private triggerShockCombo(pos: THREE.Vector3, ownerId: string) {
    this.activeExplosions.push({
      position: pos.clone(),
      radius: 9.0,
      damage: 140,
      ownerId,
    });

    // Massive glowing shock ring effect
    const ringGeo = new THREE.RingGeometry(0.5, 6.0, 24);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0xbf00ff,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.9,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.position.copy(pos);
    this.scene.add(ring);
    this.beams.push({ mesh: ring, lifetime: 0.25 });
  }

  public update(deltaTime: number) {
    this.activeExplosions = [];

    // Update Beams
    for (let i = this.beams.length - 1; i >= 0; i--) {
      const b = this.beams[i];
      b.lifetime -= deltaTime;
      if (b.lifetime <= 0) {
        this.scene.remove(b.mesh);
        this.beams.splice(i, 1);
      }
    }

    // Update Projectiles
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i];
      p.lifetime -= deltaTime;

      // Move projectile
      p.mesh.position.addScaledVector(p.velocity, deltaTime);

      // Low gravity on flak and alt bombs
      if (p.isBouncing || (p.type === 'rocket' && p.damage === 90)) {
        p.velocity.y -= 9.8 * deltaTime * 0.7; // low gravity
      }

      if (p.lifetime <= 0) {
        if (p.type === 'rocket') {
          fpsAudio.playSound('explosion');
          this.activeExplosions.push({
            position: p.mesh.position.clone(),
            radius: 6.5,
            damage: p.damage,
            ownerId: p.ownerId,
          });
        }
        this.scene.remove(p.mesh);
        this.projectiles.splice(i, 1);
      }
    }
  }

  public removeProjectile(index: number) {
    if (index >= 0 && index < this.projectiles.length) {
      const p = this.projectiles[index];
      if (p.type === 'rocket') {
        fpsAudio.playSound('explosion');
        this.activeExplosions.push({
          position: p.mesh.position.clone(),
          radius: 6.5,
          damage: p.damage,
          ownerId: p.ownerId,
        });
      }
      this.scene.remove(p.mesh);
      this.projectiles.splice(index, 1);
    }
  }
}
